import { useCallback, useEffect, useRef, useState } from "react";
import { GameState } from "../types/game";

/**
 * Custom hook for tracking game play time
 * Automatically handles timer based on game state changes
 * Returns timeTaken in seconds and control functions for timer management
 */
export const useGameTimer = (gameState: GameState) => {
  // Timer state for tracking game play time
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerStartTimeRef = useRef<number | null>(null);
  const accumulatedTimeRef = useRef<number>(0); // Time in milliseconds
  const isTimerRunningRef = useRef<boolean>(false);

  // Internal state for timeTaken in seconds
  const [timeTaken, setTimeTaken] = useState<number>(0);

  /**
   * Start the timer
   */
  const startTimer = useCallback(() => {
    if (isTimerRunningRef.current) {
      return; // Timer already running
    }

    isTimerRunningRef.current = true;
    timerStartTimeRef.current = Date.now();

    // Update timeTaken every second
    timerIntervalRef.current = setInterval(() => {
      if (timerStartTimeRef.current !== null) {
        const elapsed = Date.now() - timerStartTimeRef.current;
        const totalTime = accumulatedTimeRef.current + elapsed;
        // Update timeTaken in seconds (rounded)
        setTimeTaken(Math.round(totalTime / 1000));
      }
    }, 1000);
  }, []);

  /**
   * Pause the timer
   */
  const pauseTimer = useCallback(() => {
    if (!isTimerRunningRef.current || timerStartTimeRef.current === null) {
      return; // Timer not running
    }

    // Accumulate the elapsed time
    const elapsed = Date.now() - timerStartTimeRef.current;
    accumulatedTimeRef.current += elapsed;

    // Clear the interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    isTimerRunningRef.current = false;
    timerStartTimeRef.current = null;

    // Update final timeTaken
    setTimeTaken(Math.round(accumulatedTimeRef.current / 1000));
  }, []);

  /**
   * Resume the timer
   */
  const resumeTimer = useCallback(() => {
    if (isTimerRunningRef.current) {
      return; // Timer already running
    }

    isTimerRunningRef.current = true;
    timerStartTimeRef.current = Date.now();

    // Update timeTaken every second
    timerIntervalRef.current = setInterval(() => {
      if (timerStartTimeRef.current !== null) {
        const elapsed = Date.now() - timerStartTimeRef.current;
        const totalTime = accumulatedTimeRef.current + elapsed;
        // Update timeTaken in seconds (rounded)
        setTimeTaken(Math.round(totalTime / 1000));
      }
    }, 1000);
  }, []);

  /**
   * Stop the timer
   */
  const stopTimer = useCallback(() => {
    if (isTimerRunningRef.current && timerStartTimeRef.current !== null) {
      // Accumulate the elapsed time
      const elapsed = Date.now() - timerStartTimeRef.current;
      accumulatedTimeRef.current += elapsed;
    }

    // Clear the interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    isTimerRunningRef.current = false;
    timerStartTimeRef.current = null;

    // Update final timeTaken in seconds
    setTimeTaken(Math.round(accumulatedTimeRef.current / 1000));
  }, []);

  /**
   * Reset the timer
   */
  const resetTimer = useCallback(() => {
    // Stop timer if running
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    isTimerRunningRef.current = false;
    timerStartTimeRef.current = null;
    accumulatedTimeRef.current = 0;

    // Reset timeTaken to 0
    setTimeTaken(0);
  }, []);

  /**
   * Check if timer is currently running
   */
  const isRunning = useCallback(() => {
    return isTimerRunningRef.current;
  }, []);

  /**
   * Get accumulated time (for checking if we should resume or start fresh)
   */
  const hasAccumulatedTime = useCallback(() => {
    return accumulatedTimeRef.current > 0;
  }, []);

  // Handle timer based on game state changes
  useEffect(() => {
    switch (gameState) {
      case GameState.PLAYING:
        // Start or resume timer when game is playing
        if (!isTimerRunningRef.current) {
          // Check if we have accumulated time (resuming) or starting fresh
          if (accumulatedTimeRef.current > 0) {
            resumeTimer();
          } else {
            startTimer();
          }
        }
        break;
      case GameState.PAUSED:
        // Pause timer when game is paused
        if (isTimerRunningRef.current) {
          pauseTimer();
        }
        break;
      case GameState.GAME_OVER:
        // Stop timer when game is over
        if (isTimerRunningRef.current) {
          stopTimer();
        }
        break;
      case GameState.MENU:
        // Don't run timer in menu state
        if (isTimerRunningRef.current) {
          pauseTimer();
        }
        break;
      default:
        // For other states, pause timer if running
        if (isTimerRunningRef.current) {
          pauseTimer();
        }
        break;
    }
  }, [gameState, startTimer, pauseTimer, resumeTimer, stopTimer]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  return {
    timeTaken,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    resetTimer,
    isRunning,
    hasAccumulatedTime,
  };
};
